import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { authService } from "@/services";
import { useUserStore } from "@/store";
import { storeToRefs } from "pinia";
import { FEATURE_NAMES, logInfo, logError } from "@/loggers";

export const useAxiosAuthentication = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (request: InternalAxiosRequestConfig) => {
      const userStore = useUserStore();
      const { user } = storeToRefs(userStore);
      const token = user.value.access_token || (await authService.getAccessToken());
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    },
    (err: AxiosError) => {
      logError(FEATURE_NAMES.MVS, `Interceptor: Request interceptor error: ${err?.message || "Unknown error"}`);
      throw err ?? new Error("An error occurred during authentication request interceptor");
    }
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const status = error?.response?.status;
      const headers = (error?.config as any)?.headers || {};
      const suppressAuthRedirect = headers["X-Suppress-Auth-Redirect"] || headers["x-suppress-auth-redirect"];
      const requestUrl = error?.config?.url || "unknown URL";
      const method = error?.config?.method?.toUpperCase() || "UNKNOWN";

      if (status === 401) {
        if (suppressAuthRedirect) {
          logInfo(
            FEATURE_NAMES.MVS,
            `Interceptor: 401 response suppressed from triggering auto-login due to X-Suppress-Auth-Redirect header | ${method} ${requestUrl}`
          );
          throw error;
        } else {
          logInfo(
            FEATURE_NAMES.MVS,
            `Interceptor: 401 response triggering auto-login redirect | ${method} ${requestUrl}`
          );
          await authService.login();
        }
      }

      logError(
        FEATURE_NAMES.MVS,
        `Interceptor: Request failed | ${method} ${requestUrl} | Status: ${status || "undefined"}`
      );

      throw error ?? new Error("Request failed");
    }
  );
};
